import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faSyncAlt, faBookOpen, faQuestionCircle,
  faFilter, faBalanceScale, faArrowRight, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import abacusService from '../../services/abacusService';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import '../../components/ui/HMSTable.css';

const HELP_CONTENT = {
  title: 'General Ledger Journal',
  body: 'This tab lets you query General Ledger journal entries directly from Abacus. Select a date range and optionally filter by account ID, then load the entries. When an account ID is provided, a balance summary card will appear showing total debits, credits, and net balance for that account over the selected period. GL data can be large, so loading is always explicit.',
};

const HelpModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="md-modal-overlay" onClick={onClose}>
      <div className="md-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="md-modal-header" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
          <div className="md-modal-header-text">
            <FontAwesomeIcon icon={faQuestionCircle} className="md-modal-header-icon" />
            <h2>{HELP_CONTENT.title}</h2>
          </div>
          <button className="md-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="md-modal-body">
          <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.7 }}>{HELP_CONTENT.body}</p>
        </div>
        <div className="md-modal-footer">
          <button className="md-btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};
const defaultTo = () => new Date().toISOString().slice(0, 10);

const formatAmount = (val) => {
  if (val === null || val === undefined) return '-';
  return Number(val).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AbacusGLJournalTab = ({ books }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [accountId, setAccountId] = useState('');

  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [stoppedEarly, setStoppedEarly] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { books };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (accountId) params.account_id = parseInt(accountId, 10);

      const data = await abacusService.getGLEntries(params);
      setEntries(data.entries || []);
      setStoppedEarly(Boolean(data.stopped_early));
      setLoaded(true);

      if (accountId) {
        setBalanceLoading(true);
        try {
          const bal = await abacusService.getAccountBalance(
            books,
            parseInt(accountId, 10),
            dateFrom || null,
            dateTo || null
          );
          setBalance(bal);
        } catch {
          setBalance(null);
        } finally {
          setBalanceLoading(false);
        }
      } else {
        setBalance(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch GL entries from Abacus');
    } finally {
      setLoading(false);
    }
  }, [books, dateFrom, dateTo, accountId]);

  const getDebitAccount = (entry) => entry?.Debit?.AccountId || entry?.DebitAccountId || '-';
  const getCreditAccount = (entry) => entry?.Credit?.AccountId || entry?.CreditAccountId || '-';
  const getAmount = (entry) => entry?.Amount?.KeyAmount || entry?.KeyAmount || entry?.Amount || 0;
  const getDescription = (entry) => {
    if (entry?.Texts) {
      if (Array.isArray(entry.Texts)) return entry.Texts[0]?.Text || '';
      if (typeof entry.Texts === 'string') return entry.Texts;
      return entry.Texts?.Text || '';
    }
    return entry?.Description || '';
  };

  const filtered = entries.filter(e => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      String(getDebitAccount(e)).includes(term) ||
      String(getCreditAccount(e)).includes(term) ||
      String(e.JournalId || '').includes(term) ||
      String(e.DocumentNumber || '').toLowerCase().includes(term) ||
      getDescription(e).toLowerCase().includes(term)
    );
  });

  return (
    <>
      <div className="abacus-lookup-card">
        <div className="abacus-lookup-card__header">
          <FontAwesomeIcon icon={faFilter} style={{ color: '#0ea5e9', marginRight: 8 }} />
          <span>GL Journal Filters</span>
        </div>
        <div className="abacus-lookup-card__body">
          <div className="abacus-filter-group">
            <label className="abacus-filter-label">From</label>
            <InvoiceDatePicker
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              name="dateFrom"
              placeholder="Start date"
              className="abacus-date-picker"
            />
          </div>
          <div className="abacus-filter-group">
            <label className="abacus-filter-label">To</label>
            <InvoiceDatePicker
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              name="dateTo"
              placeholder="End date"
              className="abacus-date-picker"
            />
          </div>
          <div className="abacus-filter-group">
            <label className="abacus-filter-label">Account ID (optional)</label>
            <input
              type="number"
              className="abacus-filter-input"
              placeholder="e.g. 1000"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              style={{ width: 130 }}
            />
          </div>
          <button className="abacus-action-btn abacus-action-btn--primary" onClick={fetchEntries} disabled={loading}>
            <FontAwesomeIcon icon={loading ? faSyncAlt : faFilter} spin={loading} />
            Load Entries
          </button>
        </div>
      </div>

      {balance && (
        <div className="abacus-balance-summary">
          <div className="abacus-balance-summary__title">
            <FontAwesomeIcon icon={faBalanceScale} style={{ marginRight: 8, color: '#0ea5e9' }} />
            Account {balance.account_id} Balance
          </div>
          <div className="abacus-balance-summary__row">
            <div className="abacus-balance-item">
              <span className="abacus-balance-item__label">Total Debit</span>
              <span className="abacus-balance-item__value abacus-balance-item__value--debit">{formatAmount(balance.total_debit)}</span>
            </div>
            <div className="abacus-balance-item">
              <FontAwesomeIcon icon={faArrowRight} style={{ color: '#9ca3af', fontSize: 14 }} />
            </div>
            <div className="abacus-balance-item">
              <span className="abacus-balance-item__label">Total Credit</span>
              <span className="abacus-balance-item__value abacus-balance-item__value--credit">{formatAmount(balance.total_credit)}</span>
            </div>
            <div className="abacus-balance-item">
              <FontAwesomeIcon icon={faArrowRight} style={{ color: '#9ca3af', fontSize: 14 }} />
            </div>
            <div className="abacus-balance-item">
              <span className="abacus-balance-item__label">Net Balance</span>
              <span className={`abacus-balance-item__value ${balance.net_balance >= 0 ? 'abacus-balance-item__value--debit' : 'abacus-balance-item__value--credit'}`}>
                {formatAmount(balance.net_balance)}
              </span>
            </div>
            <div className="abacus-balance-item">
              <span className="abacus-balance-item__label">Entries</span>
              <span className="abacus-balance-item__value">{balance.entry_count}</span>
            </div>
          </div>
        </div>
      )}
      {balanceLoading && (
        <div className="abacus-balance-summary" style={{ textAlign: 'center', padding: '16px' }}>
          <FontAwesomeIcon icon={faSyncAlt} spin style={{ marginRight: 8, color: '#6b7280' }} />
          <span style={{ color: '#6b7280', fontSize: 13 }}>Loading balance...</span>
        </div>
      )}

      {error && (
        <div className="abacus-error-banner">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{error}</span>
        </div>
      )}

      {loaded && stoppedEarly && (
        <div className="abacus-notice-banner" role="status">
          Abacus can't be asked for ledger lines by date, so IMT read the first 500 lines only and
          this list may be missing older or newer ones. Open the ledger in Abacus for the full picture.
        </div>
      )}
      {loaded && entries.length > 0 && (
        <div className="search-filter-controls">
          <div className="master-data-search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="master-data-search-input"
              placeholder="Search by account, journal, document, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <span className="abacus-result-count">{filtered.length} of {entries.length} entries</span>
        </div>
      )}

      <div className="invoice-list-container">
        <div className="abacus-card">
          <div className="abacus-section-header">
            <div className="abacus-section-header__left">
              <FontAwesomeIcon icon={faBookOpen} style={{ color: '#0ea5e9' }} />
              <span className="abacus-section-header__title">General Ledger Entries</span>
              {loaded && <span className="abacus-section-header__count">{filtered.length}</span>}
            </div>
            <button className="abacus-section-header__help" title="About this tab" onClick={() => setHelpOpen(true)}>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
          </div>

          {!loaded && !loading ? (
            <div className="empty-state-container">
              <div className="empty-state-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
              <div className="empty-state-text">
                <h3>Load GL Entries from Abacus</h3>
                <p>Select a date range and optionally filter by account ID, then click "Load Entries". GL data can be large, so explicit loading is required.</p>
              </div>
            </div>
          ) : loading ? (
            <div className="abacus-card-loading">
              <HMSSkeleton lines={6} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state-container">
              <div className="empty-state-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
              <div className="empty-state-text">
                <h3>{searchTerm ? 'No Matching Entries' : 'No GL Entries Found'}</h3>
                <p>{searchTerm ? 'Try a different search term' : 'No entries found for the selected date range'}</p>
              </div>
            </div>
          ) : (
            <div className="hms-table-wrap">
              <table className="hms-table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Journal ID</th>
                    <th scope="col">Document No.</th>
                    <th scope="col">Debit Account</th>
                    <th scope="col">Credit Account</th>
                    <th scope="col" className="hms-table-num">Amount</th>
                    <th scope="col">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry, i) => (
                    <tr key={entry.Id || i}>
                      <td className="abacus-date-cell">{formatDate(entry.Date)}</td>
                      <td className="abacus-mono-cell">{entry.JournalId || '-'}</td>
                      <td className="invoice-number-cell">{entry.DocumentNumber || '-'}</td>
                      <td className="abacus-mono-cell">{getDebitAccount(entry)}</td>
                      <td className="abacus-mono-cell">{getCreditAccount(entry)}</td>
                      <td className="hms-table-num abacus-amount-cell">{formatAmount(getAmount(entry))}</td>
                      <td className="abacus-description-cell" title={getDescription(entry)}>
                        {getDescription(entry) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default AbacusGLJournalTab;
